import { createAuth0 } from '@auth0/auth0-vue';
import { watch } from 'vue';
import { trace } from '@/utils/authTrace';

// Single Auth0 client instance, created here (instead of inline in main.js) so
// non-component modules — e.g. the Cloud Functions auth interceptor and the
// Firebase bridge — can read the caller's ID token via the reactive
// auth0.idTokenClaims ref (.value.__raw). NOTE: the auth0-vue plugin exposes
// NO getIdTokenClaims() method — only that ref, refreshed after each
// login/checkSession. main.js installs this same instance with
// app.use(auth0), and useAuth0() inside components injects the very same client.
//
// NOTE: we deliberately do NOT set an `audience`. Requesting the Auth0
// Management API audience (…/api/v2/) from a SPA fails with
// `access_denied: Service not found` — a SPA cannot obtain a Management API
// access token — which broke loginWithPopup entirely. Instead we authenticate
// with plain OIDC and prove identity to our own Cloud Functions by sending the
// signed ID token (verified server-side in verifyAuth0Token by issuer +
// audience = this SPA's client id). See functionAuthInterceptor.js.
// #71 (2026-08-03): useRefreshTokens was false, so a session's only path back
// to a valid ID token was Auth0's hidden-iframe silent-auth flow — which
// needs first-party cookies AND fails outright in an installed/standalone
// PWA on iOS (WKWebView blocks the SSO cookie the iframe depends on) and is
// increasingly unreliable in-browser too as third-party/storage-partitioning
// tightens. offline_access in scope is what actually gets Auth0 to issue a
// refresh token in the first place; useRefreshTokens: true is what makes the
// SDK use it (silently, via a real token-endpoint POST) instead of the
// iframe. Requires "Refresh Token Rotation" enabled on this SPA application
// in the Auth0 dashboard — see #71's numbered dashboard steps.
//
// #73 (2026-08-03): useRefreshTokensFallback: false is already the installed
// SDK's own default (@auth0/auth0-spa-js@2.1.3, Auth0Client.ts:137) — pinned
// here explicitly anyway, because the fallback it disables is exactly the
// hidden-iframe path #71 moved us off. With it false, a refresh-token
// cache-miss throws MissingRefreshTokenError synchronously, from a local
// cache read, no network (Auth0Client.ts:957-965) instead of silently
// opening an iframe that depends on third-party-cookie access — precisely
// what iOS storage partitioning breaks. Cache-miss must fail fast to the
// login screen, never hang on an iframe that can't succeed there.
export const auth0 = createAuth0({
  domain: process.env.VUE_APP_AUTH0_SPA_DOMAIN,
  clientId: process.env.VUE_APP_AUTH0_SPA_CLIENT_ID,
  authorizationParams: {
    redirect_uri: window.location.origin,
    scope: 'openid profile email offline_access',
  },
  useRefreshTokens: true,
  useRefreshTokensFallback: false,
  cacheLocation: 'localstorage',
});

// #73 (2026-08-03): the plugin's own boot-time restore — auth0-vue's
// install() -> __checkSession() -> this._client.checkSession() — gates on a
// short-lived, JS-readable `auth0.{clientId}.is.authenticated` cookie
// (Auth0Client.ts:569-587, DEFAULT_SESSION_CHECK_EXPIRY_DAYS = 1) BEFORE it
// ever touches the localStorage token cache or attempts a refresh. If that
// cookie is gone for any reason (closed tab/app, storage partitioning, just
// expired), checkSession() returns having done nothing — isAuthenticated
// never flips true, even with a perfectly valid cached refresh token sitting
// in localStorage. The SDK's own doc comment on checkSession() names this
// failure mode and prescribes the fix: call getTokenSilently() directly
// instead, which is NOT cookie-gated and goes straight to the cache/refresh
// exchange. sessionRestoreAttempted makes this a once-per-app-load probe,
// not a per-navigation cost — see the "logged-out cost" note in #73.
let sessionRestoreAttempted = false;

// Dispatch Brief v2, Phase 2 (2026-08-05) — Addendum item 2. plugin.ts's own
// __checkSession catches every rejection from handleRedirectCallback() (an
// invalid_grant from a reused/burned authorization code being the specific
// failure this predicts) and swallows it into a redirect, never a thrown
// error the app sees. auth0.error is the ONLY surface that exception still
// reaches — Auth0Plugin's __proxy sets it in the catch block before
// re-throwing, and __checkSession's own try/catch is what stops that
// re-throw from ever reaching app code. Watching it here is the only way to
// see that failure in the trace at all.
watch(auth0.error, (err) => {
  if (!err) return;
  trace('auth0-error-ref-changed', {
    message: err.message,
    error: err.error,
    error_description: err.error_description,
  });
});

// #69 (2026-08-04, Phase-A2, [Z-B]) — the iPad live finding: credentials
// accepted, then the popup strands on a white page at the Auth0 URL and
// never returns to the app. Hypothesis: the web_message/window.opener
// hand-back loginWithPopup depends on fails across the standalone<->popup
// boundary on iPadOS. This predicate is the ONLY place in the app that
// encodes that device/platform judgment — startLogin() (./startLogin.js)
// never inspects platform itself, it just asks this function, so widening
// or narrowing the condition as Connor's remaining device differentials
// (iPad standalone-vs-Safari, OS versions, retry behaviour) come in is a
// one-line change here, not a flow-logic change anywhere else.
//
// Conservative default until those facts land: redirect for EVERY
// iOS-family context (Safari or standalone — the failure mode named above
// is the opener handshake itself, not standalone-only, and a plain iOS
// Safari popup uses that same mechanism and is currently unverified, not
// presumed safe) AND every standalone context on any platform (Android
// standalone is also popup-inside-installed-PWA, the same class of risk,
// currently untested — see #69's pending Android standalone re-run).
// Desktop and Android in-browser both stay on popup — today's working
// production behaviour, untouched by this predicate returning false there.
function isIOSFamily() {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  if (!nav) return false;
  const ua = nav.userAgent || '';
  // iPadOS has masqueraded as "Macintosh" in both platform and userAgent
  // since iPadOS 13 (Apple's own compatibility choice — no iPad-specific
  // UA token exists on current iPadOS) — the standard runtime idiom for
  // telling it apart from a real Mac is checking maxTouchPoints on a
  // claimed Mac platform, since no real Mac desktop/laptop ships a
  // touchscreen. `navigator.platform` is a deprecated API (superseded by
  // User-Agent Client Hints) but remains the only cross-browser signal
  // for this specific check — still current practice for this exact idiom.
  const isMacMasqueradingAsIPad = nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || isMacMasqueradingAsIPad;
}

function isStandalone() {
  const hasMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const displayModeStandalone = hasMatchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // navigator.standalone — the pre-display-mode-media-query iOS Safari flag
  // for "added to home screen", still the only signal on older iOS/iPadOS.
  const iosLegacyStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  return Boolean(displayModeStandalone || iosLegacyStandalone);
}

export function shouldUseRedirectLogin() {
  return isIOSFamily() || isStandalone();
}

export async function ensureSessionRestored() {
  if (sessionRestoreAttempted) return;
  sessionRestoreAttempted = true;
  trace('ensure-session-restored-start', { isLoading: auth0.isLoading.value });

  // main.js does `.use(auth0)` (constructing the real Auth0Client
  // synchronously, plugin.ts's install()) before `.mount('#app')`, which is
  // what triggers vue-router's first navigation — so the client itself is
  // always safely callable here. What is NOT guaranteed settled yet is
  // isLoading: install() fires the plugin's own __checkSession() unawaited,
  // so on the very first navigation isLoading can still be its initial
  // `true` when this runs. Reading isAuthenticated before that settles risks
  // a boot race: checkSession()'s cookie-based restore could flip
  // isAuthenticated true a moment after we've already decided (based on the
  // stale `false`) not to restore/bounce, with nothing left listening for
  // the change. Only the very first call can ever see isLoading still true —
  // the flag above makes every later call return before reaching this line.
  if (auth0.isLoading.value) {
    trace('waiting-for-isLoading-false');
    await new Promise((resolve) => {
      const unwatch = watch(auth0.isLoading, (loading) => {
        if (!loading) {
          unwatch();
          trace('isLoading-flipped-false');
          resolve();
        }
      });
    });
  }

  if (auth0.isAuthenticated.value) {
    trace('ensure-session-restored-already-authenticated');
    return;
  }

  trace('getAccessTokenSilently-start', { caller: 'ensureSessionRestored' });
  const startedAt = performance.now();
  try {
    await auth0.getAccessTokenSilently();
    trace('getAccessTokenSilently-resolved', {
      caller: 'ensureSessionRestored',
      durationMs: Math.round(performance.now() - startedAt),
    });
  } catch (e) {
    // MissingRefreshTokenError (genuinely logged out / cache-miss, thrown
    // synchronously with no network — see useRefreshTokensFallback above) or
    // a real expired/invalid refresh token. Either way there is no session
    // to restore; fall through to the guard's existing not-authenticated ->
    // login redirect.
    trace('getAccessTokenSilently-rejected', {
      caller: 'ensureSessionRestored',
      durationMs: Math.round(performance.now() - startedAt),
      message: e && e.message,
      error: e && e.error,
    });
  }
}
